import {
  useEffect, useState, useCallback, useMemo,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/api/client';
import { inviteApi } from '@/api/inviteApi';
import { telegramApi } from '@/api/telegramApi';
import { useToastContext } from '@/hooks/useToastContext';
import { logger } from '@/utils/logger';
import { TEMPLATES, DEFAULT_TEMPLATE } from '@/constants/templates';
import { validateInviteForm } from '@/utils/validation';
import type { InviteData } from '@/types/invite';
import type { RSVPResponse } from '@/types/rsvp';
import type { UpdateInviteRequest } from '@/api/inviteApi';
import styles from './AdminPage.module.css';

type AdminInviteData = InviteData & {
  rsvps: RSVPResponse[];
};

/**
 * Модальное окно для редактирования приглашения
 */
type EditInviteModalProps = {
  invite: AdminInviteData;
  onSave: (updates: UpdateInviteRequest) => void;
  onCancel: () => void;
  loading: boolean;
};

const EditInviteModal = ({
  invite, onSave, onCancel, loading,
}: EditInviteModalProps) => {
  const [formData, setFormData] = useState({
    brideName: invite.brideName,
    groomName: invite.groomName,
    weddingDate: invite.weddingDate,
    weddingLocation: invite.weddingLocation,
    template: invite.template || DEFAULT_TEMPLATE,
  });

  // Обновляем форму при изменении invite
  useEffect(() => {
    setFormData({
      brideName: invite.brideName,
      groomName: invite.groomName,
      weddingDate: invite.weddingDate,
      weddingLocation: invite.weddingLocation,
      template: invite.template || DEFAULT_TEMPLATE,
    });
  }, [invite]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();

    const validation = validateInviteForm(formData);
    if (!validation.success) {
      return;
    }

    const updates: UpdateInviteRequest = {
      ...validation.data!,
    };

    onSave(updates);
  }, [formData, onSave]);

  const isFormValid = useMemo(() => formData.brideName.trim() !== ''
      && formData.groomName.trim() !== ''
      && formData.weddingDate !== ''
      && formData.weddingLocation.trim() !== '', [formData]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={styles.modalOverlay}
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className={styles.modalContent}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className={styles.modalTitle}>Редактировать приглашение</h2>
        <form onSubmit={handleSubmit} className={styles.editForm}>
          <div className={styles.formGroup}>
            <label htmlFor="edit-brideName" className={styles.label}>
              Имя невесты
              <input
                type="text"
                id="edit-brideName"
                name="brideName"
                value={formData.brideName}
                onChange={handleChange}
                required
                className={styles.input}
              />
            </label>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="edit-groomName" className={styles.label}>
              Имя жениха
              <input
                type="text"
                id="edit-groomName"
                name="groomName"
                value={formData.groomName}
                onChange={handleChange}
                required
                className={styles.input}
              />
            </label>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="edit-weddingDate" className={styles.label}>
              Дата свадьбы
              <input
                type="date"
                id="edit-weddingDate"
                name="weddingDate"
                value={formData.weddingDate}
                onChange={handleChange}
                required
                className={styles.input}
              />
            </label>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="edit-weddingLocation" className={styles.label}>
              Место проведения
              <input
                type="text"
                id="edit-weddingLocation"
                name="weddingLocation"
                value={formData.weddingLocation}
                onChange={handleChange}
                required
                className={styles.input}
              />
            </label>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="edit-template" className={styles.label}>
              Шаблон приглашения
              <select
                id="edit-template"
                name="template"
                value={formData.template || DEFAULT_TEMPLATE}
                onChange={handleChange}
                className={styles.input}
              >
                {TEMPLATES.map((template) => (
                  <option key={template.value} value={template.value}>
                    {template.label}
                    {' '}
                    -
                    {template.description}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className={styles.modalActions}>
            <button
              type="button"
              onClick={onCancel}
              className={styles.cancelButton}
              disabled={loading}
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={loading || !isFormValid}
              className={styles.saveButton}
            >
              {loading ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

/**
 * Модальное окно для массовой отправки сообщений в Telegram
 */
type BroadcastModalProps = {
  onClose: () => void;
  onSend: (message: string) => Promise<void>;
  loading: boolean;
};

const BroadcastModal = ({ onClose, onSend, loading }: BroadcastModalProps) => {
  const [message, setMessage] = useState('');

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim().length === 0) {
      return;
    }
    await onSend(message.trim());
    setMessage('');
  }, [message, onSend]);

  const isFormValid = useMemo(() => message.trim().length > 0, [message]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={styles.modalOverlay}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className={styles.modalContent}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className={styles.modalTitle}>Массовая отправка сообщений</h2>
        <p className={styles.broadcastDescription}>
          Сообщение будет отправлено всем пользователям, которые создали приглашения через Telegram бота.
        </p>
        <form onSubmit={handleSubmit} className={styles.editForm}>
          <div className={styles.formGroup}>
            <label htmlFor="broadcast-message" className={styles.label}>
              Текст сообщения
              <textarea
                id="broadcast-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                className={styles.textarea}
                rows={6}
                placeholder="Введите текст сообщения для отправки..."
                disabled={loading}
              />
            </label>
          </div>

          <div className={styles.modalActions}>
            <button
              type="button"
              onClick={onClose}
              className={styles.cancelButton}
              disabled={loading}
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={loading || !isFormValid}
              className={styles.saveButton}
            >
              {loading ? 'Отправка...' : 'Отправить'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

/**
 * Модальное окно подтверждения удаления
 */
type DeleteConfirmModalProps = {
  inviteName: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
};

const DeleteConfirmModal = ({
  inviteName, onConfirm, onCancel, loading,
}: DeleteConfirmModalProps) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className={styles.modalOverlay}
    onClick={onCancel}
  >
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.9, opacity: 0 }}
      className={styles.modalContent}
      onClick={(e) => e.stopPropagation()}
    >
      <h2 className={styles.modalTitle}>Подтверждение удаления</h2>
      <p className={styles.deleteMessage}>
        Вы уверены, что хотите удалить приглашение для
        {' '}
        <strong>{inviteName}</strong>
        ?
      </p>
      <p className={styles.deleteWarning}>
        Это действие нельзя отменить. Все связанные RSVP ответы также будут удалены.
      </p>
      <div className={styles.modalActions}>
        <button
          type="button"
          onClick={onCancel}
          className={styles.cancelButton}
          disabled={loading}
        >
          Отмена
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className={styles.deleteConfirmButton}
          disabled={loading}
        >
          {loading ? 'Удаление...' : 'Удалить'}
        </button>
      </div>
    </motion.div>
  </motion.div>
);

/**
 * Админская страница для просмотра всех приглашений и RSVP ответов
 */
const AdminPage = () => {
  const [invites, setInvites] = useState<AdminInviteData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingInvite, setEditingInvite] = useState<AdminInviteData | null>(null);
  const [deletingInviteId, setDeletingInviteId] = useState<string | null>(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [broadcastLoading, setBroadcastLoading] = useState(false);
  const { user, logout } = useAuth();
  const { showSuccess, showError } = useToastContext();

  const fetchInvites = useCallback(async () => {
    try {
      const data = await apiClient.get<AdminInviteData[]>('/admin/invites');
      setInvites(data);
    } catch (err) {
      logger.error('Ошибка загрузки приглашений:', err);
      const errorMessage = err instanceof Error
        ? err.message
        : 'Произошла ошибка при загрузке данных';
      setError(errorMessage);

      // Если ошибка авторизации, перенаправляем на страницу входа
      if (err instanceof Error && errorMessage.includes('авторизац')) {
        logout();
      }
    }
  }, [logout]);

  useEffect(() => {
    const loadInvites = async () => {
      setLoading(true);
      await fetchInvites();
      setLoading(false);
    };

    loadInvites();
  }, [fetchInvites]);

  const formatDate = (timestamp: number): string => new Date(timestamp).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const formatWeddingDate = (dateString: string): string => new Date(dateString).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const getTotalGuests = (rsvps: RSVPResponse[]): number => rsvps
    .filter((rsvp) => rsvp.response === 'yes')
    .reduce((total, rsvp) => total + rsvp.guestCount, 0);

  const handleEdit = useCallback((invite: AdminInviteData) => {
    setEditingInvite(invite);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingInvite(null);
  }, []);

  const handleUpdate = useCallback(async (updates: UpdateInviteRequest) => {
    if (!editingInvite) return;

    setUpdateLoading(true);
    try {
      const result = await inviteApi.update(editingInvite.id, updates);
      if (result.success) {
        showSuccess('Приглашение успешно обновлено');
        await fetchInvites();
        setEditingInvite(null);
      }
    } catch (err) {
      const errorMessage = err instanceof Error
        ? err.message
        : 'Произошла ошибка при обновлении приглашения';
      showError(errorMessage);
      logger.error('Ошибка обновления приглашения:', err);
    } finally {
      setUpdateLoading(false);
    }
  }, [editingInvite, fetchInvites, showSuccess, showError]);

  const handleDeleteClick = useCallback((inviteId: string) => {
    setDeletingInviteId(inviteId);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setDeletingInviteId(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deletingInviteId) return;

    setDeleteLoading(true);
    try {
      const result = await inviteApi.delete(deletingInviteId);
      if (result.success) {
        showSuccess('Приглашение успешно удалено');
        await fetchInvites();
        setDeletingInviteId(null);
      }
    } catch (err) {
      const errorMessage = err instanceof Error
        ? err.message
        : 'Произошла ошибка при удалении приглашения';
      showError(errorMessage);
      logger.error('Ошибка удаления приглашения:', err);
    } finally {
      setDeleteLoading(false);
    }
  }, [deletingInviteId, fetchInvites, showSuccess, showError]);

  const handleBroadcast = useCallback(async (message: string) => {
    setBroadcastLoading(true);
    try {
      const result = await telegramApi.broadcast({ message });
      if (result.success) {
        showSuccess(
          `Сообщение отправлено: ${result.sent} успешно, ${result.failed} ошибок`,
        );
        setShowBroadcastModal(false);
      } else {
        showError(result.message || 'Ошибка при отправке сообщений');
      }
    } catch (err) {
      const errorMessage = err instanceof Error
        ? err.message
        : 'Произошла ошибка при отправке сообщений';
      showError(errorMessage);
      logger.error('Ошибка массовой отправки сообщений:', err);
    } finally {
      setBroadcastLoading(false);
    }
  }, [showSuccess, showError]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Загрузка данных...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <h2>Ошибка загрузки данных</h2>
          <p>{error}</p>
          <div className={styles.errorDetails}>
            <p><strong>Возможные причины:</strong></p>
            <ul>
              <li>API недоступен или возвращает ошибку</li>
              <li>Проблемы с авторизацией (токен истек или недействителен)</li>
              <li>Проблемы с CORS</li>
            </ul>
            <p>
              <strong>Решение:</strong>
              {' '}
              Попробуйте выйти и войти снова, или проверьте подключение к интернету.
            </p>
            <Link to="/" className={styles.backLink}>
              ← Вернуться на главную
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className={styles.content}
      >
        <div className={styles.header}>
          <div className={styles.headerTop}>
            <div>
              <h1 className={styles.title}>Админ-панель</h1>
              <p className={styles.subtitle}>Просмотр всех приглашений и RSVP ответов</p>
              {user && (
                <p className={styles.userInfo}>
                  Вы вошли как:
                  {' '}
                  <strong>{user.email}</strong>
                </p>
              )}
            </div>
            <div className={styles.headerActions}>
              <button
                type="button"
                onClick={() => setShowBroadcastModal(true)}
                className={styles.broadcastButton}
              >
                📢 Массовая рассылка
              </button>
              <button
                type="button"
                onClick={logout}
                className={styles.logoutButton}
              >
                Выйти
              </button>
            </div>
          </div>
          <Link to="/" className={styles.backLink}>
            ← Вернуться на главную
          </Link>
        </div>

        {invites.length === 0 ? (
          <div className={styles.emptyState}>
            <p>Приглашения пока не созданы</p>
          </div>
        ) : (
          <div className={styles.invitesList}>
            {invites.map((invite, index) => (
              <motion.div
                key={invite.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className={styles.inviteCard}
              >
                <div className={styles.inviteHeader}>
                  <div className={styles.inviteNames}>
                    <h2>
                      {invite.brideName}
                      {' '}
                      &
                      {invite.groomName}
                    </h2>
                  </div>
                  <div className={styles.inviteMeta}>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Дата свадьбы:</span>
                      <span className={styles.metaValue}>{formatWeddingDate(invite.weddingDate)}</span>
                    </div>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Место:</span>
                      <span className={styles.metaValue}>{invite.weddingLocation}</span>
                    </div>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Создано:</span>
                      <span className={styles.metaValue}>{formatDate(invite.createdAt)}</span>
                    </div>
                    <div className={styles.inviteActions}>
                      <Link to={`/invite/${invite.id}`} className={styles.viewLink} target="_blank">
                        Посмотреть приглашение →
                      </Link>
                      <button
                        type="button"
                        onClick={() => handleEdit(invite)}
                        className={styles.editButton}
                      >
                        Редактировать
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteClick(invite.id)}
                        className={styles.deleteButton}
                      >
                        Удалить
                      </button>
                    </div>
                  </div>
                </div>

                <div className={styles.rsvpSection}>
                  <div className={styles.rsvpHeader}>
                    <h3>
                      RSVP Ответы (
                      {invite.rsvps.length}
                      )
                    </h3>
                    <div className={styles.rsvpStats}>
                      <span className={styles.statItem}>
                        Подтвердили:
                        {' '}
                        {invite.rsvps.filter((r) => r.response === 'yes').length}
                      </span>
                      <span className={styles.statItem}>
                        Отказали:
                        {' '}
                        {invite.rsvps.filter((r) => r.response === 'no').length}
                      </span>
                      <span className={styles.statItem}>
                        Всего гостей:
                        {' '}
                        {getTotalGuests(invite.rsvps)}
                      </span>
                    </div>
                  </div>

                  {invite.rsvps.length === 0 ? (
                    <div className={styles.noRsvps}>Ответов пока нет</div>
                  ) : (
                    <div className={styles.rsvpList}>
                      {invite.rsvps.map((rsvp, rsvpIndex) => (
                        <motion.div
                          key={`${invite.id}-rsvp-${rsvp.respondedAt}-${rsvp.response}-${rsvp.guestCount}`}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: rsvpIndex * 0.05, duration: 0.3 }}
                          className={`${styles.rsvpItem} ${rsvp.response === 'yes' ? styles.rsvpYes : styles.rsvpNo}`}
                        >
                          <div className={styles.rsvpResponse}>
                            <span className={styles.responseBadge}>
                              {rsvp.response === 'yes' ? '✓ Придет' : '✗ Не придет'}
                            </span>
                            <span className={styles.responseDate}>
                              {formatDate(rsvp.respondedAt)}
                            </span>
                          </div>
                          {rsvp.response === 'yes' && (
                            <div className={styles.rsvpDetails}>
                              <div className={styles.guestCount}>
                                Количество гостей:
                                {' '}
                                {rsvp.guestCount}
                              </div>
                              {rsvp.guestNames && rsvp.guestNames.length > 0 && (
                                <div className={styles.guestNames}>
                                  <strong>Имена гостей:</strong>
                                  <ul>
                                    {rsvp.guestNames.map((name) => (
                                      <li
                                        key={`${invite.id}-${rsvp.respondedAt}-guest-${name}`}
                                      >
                                        {name}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                          {rsvp.message && (
                            <div className={styles.rsvpMessage}>
                              <strong>Сообщение:</strong>
                              <p>{rsvp.message}</p>
                            </div>
                          )}
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Модальное окно редактирования */}
        <AnimatePresence>
          {editingInvite && (
            <EditInviteModal
              invite={editingInvite}
              onSave={handleUpdate}
              onCancel={handleCancelEdit}
              loading={updateLoading}
            />
          )}
        </AnimatePresence>

        {/* Модальное окно подтверждения удаления */}
        <AnimatePresence>
          {deletingInviteId && (
            <DeleteConfirmModal
              inviteName={invites.find((i) => i.id === deletingInviteId)?.brideName || ''}
              onConfirm={handleConfirmDelete}
              onCancel={handleCancelDelete}
              loading={deleteLoading}
            />
          )}
        </AnimatePresence>

        {/* Модальное окно массовой отправки */}
        <AnimatePresence>
          {showBroadcastModal && (
            <BroadcastModal
              onClose={() => setShowBroadcastModal(false)}
              onSend={handleBroadcast}
              loading={broadcastLoading}
            />
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default AdminPage;
